import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../hooks/useTheme";
import AppSidebar from "./AppSidebar";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import {
  deleteTracker,
  deleteTrackerAsync,
  fetchTrackers,
  updateTrackerAsync,
} from "../store/slices/trackersSlice";
import ConfirmationModal from "./ConfirmationModal";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { DayOfWeek, Tracker } from "../types";

const getInitials = (
  name?: string | null,
  username?: string | null,
): string => {
  if (name)
    return name
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  if (username) return username.charAt(0).toUpperCase();
  return "U";
};

const TrackerList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, setTheme } = useTheme();
  const userProfile = useAppSelector((state) => state.user.profile);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { items: trackers, loading } = useAppSelector(
    (state) => state.trackers,
  );
  const hasFetchedRef = useRef(false);
  const initials = getInitials(userProfile?.name, userProfile?.username);

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];

  // State for confirmation modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [onConfirmAction, setOnConfirmAction] = useState<(() => void) | null>(
    null,
  );

  useEffect(() => {
    trackPageVisit("tracker_list", {
      total_trackers: trackers.length,
      active_trackers: trackers.filter((t) => t.isActive).length,
    });
  }, [trackers]);

  const fetchTrackersData = useCallback(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    // Calculate date range (not really needed for trackers list, but for consistency)
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1)
      .toISOString()
      .split("T")[0];
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 31)
      .toISOString()
      .split("T")[0];

    // Fetch latest trackers
    dispatch(
      fetchTrackers({ startDate, endDate, isActive: true, withEntries: false }),
    );
  }, [dispatch]);

  useEffect(() => {
    fetchTrackersData();
  }, [fetchTrackersData]);

  const activeTrackers = useMemo(() => {
    return trackers
      .filter((tracker) => tracker.isActive)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
  }, [trackers]);

  /* const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }; */

  const formatTrackingDays = (trackingDays?: DayOfWeek[]) => {
    if (!trackingDays || trackingDays.length === 0) {
      return t("trackerList.everyDay");
    }

    const dayLabels: Record<DayOfWeek, string> = {
      monday: t("trackerList.dayLabels.monday"),
      tuesday: t("trackerList.dayLabels.tuesday"),
      wednesday: t("trackerList.dayLabels.wednesday"),
      thursday: t("trackerList.dayLabels.thursday"),
      friday: t("trackerList.dayLabels.friday"),
      saturday: t("trackerList.dayLabels.saturday"),
      sunday: t("trackerList.dayLabels.sunday"),
    };

    return trackingDays.map((day) => dayLabels[day]).join(", ");
  };

  const handleBack = () => {
    navigate("/app");
  };

  const showConfirmationModal = (message: string, onConfirm: () => void) => {
    setModalMessage(message);
    setOnConfirmAction(() => onConfirm);
    setIsModalOpen(true);
  };

  const handleModalConfirm = () => {
    if (onConfirmAction) {
      onConfirmAction();
    }
    setIsModalOpen(false);
    setOnConfirmAction(null);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setOnConfirmAction(null);
  };

  const handleEditTracker = (trackerId: string) => {
    navigate(`/edit-tracker/${trackerId}`);
  };

  const handleDeleteTracker = (trackerId: string) => {
    showConfirmationModal(t("trackerList.deleteConfirmation"), () => {
      // Delete locally first for immediate UI feedback
      dispatch(deleteTracker(trackerId));
      // Then delete on backend
      dispatch(deleteTrackerAsync(trackerId));

      // Track tracker deletion event
      trackUserInteraction("delete_tracker", "tracker_management", trackerId);
    });
  };

  const handleTogglePause = async (trackerId: string, isPaused: boolean) => {
    try {
      await dispatch(
        updateTrackerAsync({
          id: trackerId,
          updates: { isPaused },
        }),
      );
    } catch (error) {
      console.error("Error updating tracker pause status:", error);
    }
  };

  const handleAddTracker = () => {
    navigate("/add-tracker");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#f5a623] rounded-2xl mb-6">
            <svg
              className="animate-spin w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            {t("trackerList.loadingTrackers")}
          </h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaHead
        title={t("trackerList.title")}
        description={t("trackerList.description") as string}
        keywords={t("trackerList.keywords") as string}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 border border-[#3b5cde]/30 flex items-center justify-center transition-all hover:bg-[#3b5cde]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("trackerList.headerTitle")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#3b5cde]/15 border border-[#3b5cde]/25 flex items-center justify-center transition-all hover:bg-[#3b5cde]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("trackerList.headerTitle")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="max-w-4xl mx-auto pb-8">
              {/* Stats Header */}
              {activeTrackers.length > 0 && (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mb-6">
                  {/* Total Trackers */}
                  <div className="glass-card p-4 lg:p-5 rounded-xl">
                    <div className="text-[15px] lg:text-[16px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                      {t("trackerList.totalTrackers")}
                    </div>
                    <div className="text-2xl lg:text-3xl font-semibold text-neutral-900 dark:text-neutral-100">
                      {activeTrackers.length}
                    </div>
                  </div>

                  {/* Active Trackers */}
                  <div className="glass-card p-4 lg:p-5 rounded-xl">
                    <div className="text-[15px] lg:text-[16px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                      {t("trackerList.activeTrackers")}
                    </div>
                    <div className="text-2xl lg:text-3xl font-semibold text-green-600 dark:text-green-400">
                      {activeTrackers.filter((t) => !t.isPaused).length}
                    </div>
                  </div>

                  {/* Paused Trackers */}
                  {activeTrackers.some((t) => t.isPaused) && (
                    <div className="col-span-2 lg:col-span-1 glass-card p-4 lg:p-5 rounded-xl">
                      <div className="text-[15px] lg:text-[16px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                        {t("trackerList.pausedTrackers")}
                      </div>
                      <div className="text-2xl lg:text-3xl font-semibold text-orange-600 dark:text-orange-400">
                        {activeTrackers.filter((t) => t.isPaused).length}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tracker Grid */}
              {activeTrackers.length === 0 ? (
                <div className="glass-card p-12 lg:p-16 text-center rounded-xl">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#a33fe8]/10 to-[#a33fe8]/5 dark:from-[#a33fe8]/20 dark:to-[#a33fe8]/10 flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-[#a33fe8]/40"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                    {t("trackerList.noActiveTrackers")}
                  </h3>
                  <p className="text-[18px] text-neutral-600 dark:text-neutral-400 mb-6">
                    {t("trackerList.noTrackersMessage")}
                  </p>
                  <button
                    onClick={handleAddTracker}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#a33fe8] hover:bg-[#a33fe8]/90 text-white rounded-xl font-medium text-[17px] transition-colors duration-150"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    {t("trackerList.createFirstTracker")}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {activeTrackers.map((tracker: Tracker) => (
                    <div
                      key={tracker.id}
                      className="glass-card p-4 lg:p-5 rounded-xl hover:bg-black/[0.02] dark:hover:bg-white/[0.08] transition-all duration-150 group flex flex-col"
                    >
                      {/* Header with Title and Type */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1 pr-2">
                          <h3 className="text-[19px] lg:text-base font-semibold text-neutral-900 dark:text-neutral-100 leading-snug">
                            {tracker.name}
                          </h3>
                        </div>
                        <span className="px-2.5 py-1.5 text-[14px] font-medium bg-[#a33fe8]/15 text-[#a33fe8] dark:bg-[#a33fe8]/20 dark:text-[#a33fe8] rounded-full whitespace-nowrap">
                          {tracker.dataType}
                        </span>
                      </div>

                      {/* Info Grid */}
                      <div className="space-y-3 flex-1 text-[17px] mb-4">
                        {/* Unit */}
                        {tracker.unit && (
                          <div className="flex items-center space-x-2.5">
                            <div className="w-2 h-2 rounded-full bg-[#a33fe8]/40"></div>
                            <span className="text-neutral-600 dark:text-neutral-400">
                              {t("trackerList.unitLabel")}
                              <span className="font-medium text-neutral-900 dark:text-neutral-100 ml-1">
                                {tracker.unit}
                              </span>
                            </span>
                          </div>
                        )}

                        {/* Tracking Days */}
                        <div className="flex items-center space-x-2.5">
                          <div className="w-2 h-2 rounded-full bg-[#a33fe8]/40"></div>
                          <span className="text-neutral-600 dark:text-neutral-400">
                            {t("trackerList.trackingLabel")}
                            <span className="font-medium text-neutral-900 dark:text-neutral-100 ml-1">
                              {formatTrackingDays(tracker.trackingDays)}
                            </span>
                          </span>
                        </div>

                        {/* Status */}
                        <div className="flex items-center space-x-2.5">
                          <div
                            className={`w-2 h-2 rounded-full ${tracker.isPaused ? "bg-orange-500" : "bg-green-500"}`}
                          ></div>
                          <span
                            className={`font-medium text-[17px] ${
                              tracker.isPaused
                                ? "text-orange-600 dark:text-orange-400"
                                : "text-green-600 dark:text-green-400"
                            }`}
                          >
                            {tracker.isPaused
                              ? t("editTracker.trackerPaused")
                              : t("trackerList.activeStatus")}
                          </span>
                        </div>
                      </div>

                      {/* Description if exists */}
                      {tracker.description && (
                        <div className="mb-4 pb-4 border-t border-neutral-200 dark:border-neutral-700">
                          <p className="text-[16px] text-neutral-600 dark:text-neutral-400 line-clamp-2 leading-relaxed">
                            {tracker.description}
                          </p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <button
                          onClick={() => navigate(`/tracker/${tracker.id}`)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-[16px] font-medium text-neutral-600 dark:text-neutral-400 hover:text-[#a33fe8] dark:hover:text-[#a33fe8] hover:bg-[#a33fe8]/10 dark:hover:bg-[#a33fe8]/15 rounded-lg transition-colors duration-150"
                          title="View tracker details"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                          View
                        </button>
                        <button
                          onClick={() => handleEditTracker(tracker.id)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-[16px] font-medium text-neutral-600 dark:text-neutral-400 hover:text-[#3b5cde] dark:hover:text-[#3b5cde] hover:bg-[#3b5cde]/10 dark:hover:bg-[#3b5cde]/15 rounded-lg transition-colors duration-150"
                          title="Edit tracker"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            handleTogglePause(tracker.id, !tracker.isPaused)
                          }
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-[16px] font-medium rounded-lg transition-colors duration-150 ${
                            tracker.isPaused
                              ? "text-orange-600 dark:text-orange-400 hover:bg-orange-500/10 dark:hover:bg-orange-500/15"
                              : "text-neutral-600 dark:text-neutral-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-500/10 dark:hover:bg-orange-500/15"
                          }`}
                          title={
                            tracker.isPaused
                              ? "Resume tracker"
                              : "Pause tracker"
                          }
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            {tracker.isPaused ? (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M14.751 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.554.832l3.197-2.132a1 1 0 000-1.664z"
                              />
                            ) : (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            )}
                          </svg>
                          {tracker.isPaused ? "Resume" : "Pause"}
                        </button>
                        <button
                          onClick={() => handleDeleteTracker(tracker.id)}
                          className="flex items-center justify-center px-3 py-2 text-[16px] font-medium text-neutral-600 dark:text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10 dark:hover:bg-red-500/15 rounded-lg transition-colors duration-150"
                          title="Delete tracker"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
              isOpen={isModalOpen}
              message={modalMessage}
              onConfirm={handleModalConfirm}
              onCancel={handleModalCancel}
            />
          </div>
        </main>

        {/* Floating Add Tracker Button */}
        <button
          onClick={handleAddTracker}
          className="fixed bottom-24 right-8 z-50 bg-[#f5a623] hover:opacity-90 text-[#1a1300] p-3 rounded-full border border-[#f5a623] transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#f5a623] focus:ring-offset-2"
          aria-label={t("trackerList.addTrackerAriaLabel") as string}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
        </button>

        {/* MOBILE BOTTOM NAV */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border-t border-black/[0.08] dark:border-white/[0.08]">
          <div className="flex items-center justify-around px-2 pt-2 pb-2">
            {mobileNavLinks.map(({ path, emoji, label }) => {
              const active = isNavActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex flex-col items-center gap-1 cursor-pointer"
                  style={{ color: active ? "#f5a623" : undefined }}
                >
                  <span className="text-[22px]">{emoji}</span>
                  <span
                    className={`text-[13px] font-medium${active ? "" : " text-gray-400 dark:text-white/25"}`}
                    style={active ? { color: "#f5a623" } : undefined}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default TrackerList;
